/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { get, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import { pluralize } from '@linc-technologies/linced-common/utils/inflector';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';

export default class GroupGoalsTable extends Component {
  // Services
  @service
  features;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  GoalState = GoalState;
  chartsDialogOpen = false;
  bulkDialog = false;
  isGradable = false;
  isPeakTime = false;
  @tracked
  saveInProgress = false;
  tableOnly = false;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @alias('model.subject.isGradable')
  gradingEnabled;

  @tracked sortProp = 'fullName';
  @tracked sortDir = 'asc';
  queryParams = ['tab'];
  tab = 1;
  title = '';
  isTopic = false;
  @tracked
  selectedLearners = null; // Declare as null initially

  init() {
    super.init(...arguments);
    this.selectedLearners = []; // Initialize a unique array for each component instance
  }

  @alias('model.group.currentLearners.length')
  numLearners;

  @tracked
  activeSlice;

  @tracked
  sliceDialog;

  @tracked
  states;

  @tracked
  statesMap;

  @computed('states.[]', 'goals.[]', 'model.group.currentLearners.[]')
  get goalStateCounts() {
    const states = this.states || [];
    const goals = this.goals || [];
    const learners = this.model.group.currentLearners || [];

    // Create lookup maps
    const goalMap = goals.reduce((map, goal) => {
      map[goal.id] = goal;
      return map;
    }, {});

    // Filter out any learners who we don't have a person record for.
    const groupLearners = mapBy(
      uniqBy(filterBy(learners, 'person.id'), 'person.id'),
      'person',
    );

    // Initialise an object to store counts and pie chart data by goal
    let goalData = {};

    // states provided to this component should already be only the most
    // recent state for each learner-goal pair.
    states.forEach((state) => {
      const goalId = state.goal_id;
      const goal = goalMap[goalId];

      if (!goal) return; // Skip if no corresponding goal.

      const subject = get(goal, 'subject');
      const category = get(goal, 'category');
      const gradable = get(subject, 'isGradable') || this.isGradable;

      // Initialize goal data if not present.
      if (!goalData[goalId]) {
        goalData[goalId] = {
          goalId,
          subject,
          category,
          gradable,
          educatorDescription: goal.educatorDescription || `Goal ${goalId}`,
          pieChartData: [],
          gradePieChartData: [],
          learners: {
            notSet: [],
            current: [],
            secure: [],
            0: [],
            1: [],
            2: [],
            3: [],
            4: [],
            5: [],
          },
          possibleGrades: goal.possibleGrades,
        };
      }

      // Add learner to the appropriate array for their goal state.
      switch (state.awarded) {
        case GoalState.DEVELOPING:
          goalData[goalId].learners.current.push(state.person);
          break;
        case GoalState.SECURE:
          goalData[goalId].learners.secure.push(state.person);
          break;
      }

      // If graded, add learner to the appropriate array for their grade.
      if (
        gradable &&
        !isEmpty(findBy(goal.possibleGrades, 'value', state.grade))
      ) {
        goalData[goalId].learners[state.grade].push(state.person);
      }
    });

    // Determine the unset/ungraded learners.
    Object.entries(goalData).forEach(([goalId, goal]) => {
      const idsByState = new Set([
        ...mapBy(goal.learners.current, 'id'),
        ...mapBy(goal.learners.secure, 'id'),
      ]);
      goalData[goalId].learners.notSet = groupLearners.filter(
        (learner) => !idsByState.has(learner.id),
      );

      const idsByGrade = new Set(
        goal.possibleGrades.reduce((ids, grade) => {
          ids.push(...mapBy(goal.learners[grade.value], 'id'));
          return ids;
        }, []),
      );
      goalData[goalId].learners[0] = groupLearners.filter(
        (learner) => !idsByGrade.has(learner.id),
      );
    });

    // Compute pie chart data for each goal.
    Object.entries(goalData).forEach(([goalId, data]) => {
      const { educatorDescription, learners, possibleGrades } = data;
      let gradeDataArray = [];
      possibleGrades.forEach((grade) => {
        if (!isEmpty(learners[grade.value])) {
          const count = learners[grade.value].length;
          gradeDataArray.push({
            goalId,
            educatorDescription,
            value: grade.value,
            label: `${grade.string} (${count})`,
            sliceLabel: grade.string,
            count,
            numericValue: grade.value,
            tooltip: `${count} - ${grade.string}`,
            color: grade.colour,
            learners: learners[grade.value] || [],
          });
        }
      });
      if (!isEmpty(learners[0])) {
        // Add "ungraded" slice.
        const count = learners[0].length;
        gradeDataArray.push({
          goalId,
          educatorDescription,
          value: 0,
          label: `Not graded (${count})`,
          sliceLabel: 'Not graded',
          count,
          numericValue: 6, // Pushes the "not graded" slice to the end.
          tooltip: `${count} - Not graded`,
          color: '#e0e0e0',
          learners: learners[0],
        });
      }

      // Sort grades numerically.
      gradeDataArray.sort(byKeys('numericValue'));
      data.gradePieChartData.push(...gradeDataArray);

      // Add `notSet`, `current`, and `secure` categories
      const categories = [
        {
          key: 'current',
          label: 'Current',
          value: learners.current.length,
          color: '#fee9bc',
        },
        {
          key: 'secure',
          label: 'Complete',
          value: learners.secure.length,
          color: '#66bb6a',
        },
        {
          key: 'notSet',
          label: 'Not Set',
          value: learners.notSet.length,
          color: '#e0e0e0',
        },
      ];

      categories.forEach(({ key, label, value, color }) => {
        if (value > 0) {
          data.pieChartData.push({
            educatorDescription,
            value: key,
            label: `${label} (${value})`,
            sliceLabel: label,
            count: value,
            tooltip: `${pluralize(value, 'learner')} ${label.toLowerCase()}`,
            color,
            learners: learners[key] || [],
          });
        }
      });
    });

    // Convert to an array.
    return Object.values(goalData);
  }

  @computed(
    'model.group.currentLearners.[]',
    'sortProp',
    'sortDir',
    'statesMap',
  )
  get sortedLearners() {
    const { sortProp, sortDir, statesMap } = this;
    const learners = this.model.group.currentLearners || [];

    const learnersWithSortValues = learners.map((learner) => {
      let sortValue = learner[sortProp] || '';
      if (sortProp.startsWith('goal_')) {
        const goalId = sortProp.replace('goal_', '');
        const key = `${goalId}-${learner.personId}`;
        const goalState = (statesMap && statesMap[key]) || {};
        // A grade of 0 (the Go zero value) means no grade.
        sortValue = goalState.grade
          ? `a-${goalState.grade}`
          : goalState.awarded || 'unknown';
      }
      // Ensure sortValue is a string
      return { learner, sortValue: String(sortValue) };
    });

    return learnersWithSortValues
      .sort((a, b) =>
        sortDir === 'asc'
          ? a.sortValue.localeCompare(b.sortValue)
          : b.sortValue.localeCompare(a.sortValue),
      )
      .map(({ learner }) => learner);
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.refreshStates(); // Ensure `states` is always up-to-date on component initialisation
  }

  @action
  updateSort(sortProp) {
    if (this.sortProp === sortProp) {
      // Toggle sort direction
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new sort property and reset direction
      this.sortProp = sortProp;
      this.sortDir = 'asc';
    }
  }

  @action
  selectAll() {
    if (this.selectedLearners.length !== this.sortedLearners.length) {
      this.selectedLearners = this.sortedLearners.map((item) => item);
    } else {
      this.selectedLearners = [];
    }
  }

  @action
  selectLearner(learner) {
    this.selectedLearners = this.selectedLearners.includes(learner)
      ? this.selectedLearners.filter((l) => l !== learner)
      : [...this.selectedLearners, learner];
  }

  @action
  showSliceDetail(data) {
    this.activeSlice = data;
    this.sliceDialog = true;
  }

  @action
  closeSliceDetail() {
    this.sliceDialog = false;
  }

  @action
  bulkApplyGrade(grade, goal) {
    if (!this.states || !this.statesMap) {
      return this.refreshStates().then(() => {
        this.actions.bulkApply.call(this, 2, goal); // Retry bulkApply
      });
    }
    this.saveInProgress = true;
    const learners = this.selectedLearners || [];
    const statesMap = this.statesMap || {}; // Access the precomputed statesMap

    Promise.all(
      learners.map((learner) => {
        const person = learner.person;
        if (!person) {
          return Promise.resolve(); // Skip if no person is associated
        }

        // Look up the state in the statesMap
        const key = `${goal.id}-${person.id}`;
        const goalState = statesMap[key] || {};

        // Skip if grade is already awarded
        if (goalState.grade === grade) {
          return Promise.resolve();
        }
        // If grade is null, only update existing states
        if (grade === null) {
          // Skip if no existing state found
          if (!goalState.id) {
            return Promise.resolve();
          }

          // Update existing state, preserving the awarded attribute
          const existingState = this.store.peekRecord(
            'goals/state',
            goalState.id,
          );
          if (existingState) {
            existingState.set('grade', grade);
            // Keep existing awarded value unchanged
            return existingState.save();
          }
          return Promise.resolve();
        }
        // Build and save new state
        const stateParams = {
          schoolId: this.get('session.school'),
          assessorId: this.get('session.person'),
          person,
          goal,
          awarded: 3,
          grade,
        };

        const newState = this.store.createRecord('goals/state', stateParams);
        return saveOrDiscard(newState);
      }),
    )
      .then(() => {
        this.toaster.success('Bulk update successful.');
      })
      .catch(() => {
        this.toaster.error('Error updating one or more goal states.');
      })
      .finally(() => {
        this.saveInProgress = false;
        this.refreshStates(); // Refresh states after updates
      });
  }

  @action
  bulkApply(awarded, goal) {
    if (!this.states || !this.statesMap) {
      // Ensure states are refreshed before bulk apply
      return this.refreshStates().then(() => {
        this.actions.bulkApply.call(this, awarded, goal); // Retry bulkApply
      });
    }
    this.saveInProgress = true;
    const learners = this.selectedLearners || [];
    const statesMap = this.statesMap || {}; // Access the precomputed statesMap

    Promise.all(
      learners.map((learner) => {
        const person = learner.person;
        if (!person) {
          return Promise.resolve(); // Skip if no person is associated
        }

        // Look up the state in the statesMap
        const key = `${goal.id}-${person.id}`;
        const goalState = statesMap[key] || {};

        // Skip if state is already awarded to this state
        if (goalState.awarded === awarded) {
          return Promise.resolve();
        }

        // Build and save new state
        const stateParams = {
          schoolId: this.get('session.school'),
          assessorId: this.get('session.person'),
          person,
          goal,
          awarded,
          grade: goalState.grade || null,
        };

        const newState = this.store.createRecord('goals/state', stateParams);
        return saveOrDiscard(newState);
      }),
    )
      .then(() => {
        this.toaster.success('Bulk update successful.');
      })
      .catch(() => {
        this.toaster.error('Error updating one or more goal states.');
      })
      .finally(() => {
        this.saveInProgress = false;
        this.refreshStates(); // Refresh states after updates
      });
  }

  @action
  refreshStates() {
    this.saveInProgress = true;

    return Promise.all(
      this.goals.map(async (goal) => {
        const states = await goal.get('states');
        return states.slice(); // Ensure we work with a standard array
      }),
    )
      .then((allStates) => {
        const states = allStates.flat();
        // Ensure each state has valid `goal_id` and `person_id`
        const validStates = states.filter(
          (state) => state.goal_id && state.person_id,
        );
        // Build statesMap
        const statesMap = validStates.reduce((map, state) => {
          const key = `${state.goal_id}-${state.person_id}`;
          map[key] = state;
          return map;
        }, {});
        this.states = validStates; // Save valid states
        this.statesMap = statesMap; // Save the map
      })
      .catch(() => {
        this.toaster.error('Failed to refresh states. Please try again.');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  @action
  noOp() {}
  updateStates() {
    this.saveInProgress = false;
    this.states.then((states) => {
      states.update();
    });
  }
}
